import { useEffect } from 'react';
import { usePermissions, useStoreContext } from 'react-admin';
import { FORKLIFT_FILTER_DEFAULT } from './userOffers/forklift';
import { useFeatureState } from './featureFlags/FeatureGate';
import {
  FEATURE_BANK_DEFAULT_VIEWS,
  FEATURE_BANK_FILTERS,
  roleDefaultListParams,
} from './userOffers/bankFtl';

/**
 * Domyślny „Widok działu” na liście zleceń dla ról działowych (01.10.2026).
 *
 * Rozdział Trade V2.0 §19: „Trade powinien móc podejrzeć rekordy w CS,
 * Administracji i FTL, ale domyślnie pracować na własnej kolejce”; tamże
 * „każdy użytkownik otrzymuje własny domyślny widok i filtry”. Trader zaczyna
 * więc od widoku Trade, planista FTL — od widoku FTL. Master planowania,
 * administrator i zwykły użytkownik — bez domyślnego filtra, jak dotąd.
 *
 * Domyślny = tylko pierwszy widok: zapisujemy parametry listy w magazynie
 * react-admina wyłącznie wtedy, gdy ich tam nie ma. Filtr da się zdjąć,
 * a wybór użytkownika zostaje (magazyn czyści dopiero wylogowanie; przy
 * przełączniku `autoListFilters` filtry konta zostają i po nim). Klucz
 * filtra (`dzial`) i wartości (`FTL`, `TRADE`) są te same co na liście —
 * react-admin zapamiętuje je użytkownikom, więc ich nie zmieniamy.
 */

/** Klucz parametrów listy zleceń w magazynie react-admina (`<zasób>.listParams`). */
export const BANK_LIST_PARAMS_KEY = 'user-offers.listParams';

/** Wartości filtra `dzial` z listy zleceń (`DEPARTMENT_CHOICES`). */
export type Department = 'FTL' | 'TRADE';

const DEFAULT_DEPARTMENT: Readonly<Record<string, Department>> = {
  PLANNER_FTL: 'FTL',
  TRADER: 'TRADE',
  // Team Leader Trade (24.10.2026) zaczyna od widoku swojego działu.
  TRADE_LEAD: 'TRADE',
};

/** Widok działu, od którego rola zaczyna; `null` = bez domyślnego filtra. */
export function defaultDepartmentFor(role: string | null | undefined): Department | null {
  if (!role || !Object.prototype.hasOwnProperty.call(DEFAULT_DEPARTMENT, role)) {
    return null;
  }
  return DEFAULT_DEPARTMENT[role];
}

/**
 * Parametry listy z filtrem działu. Filtr wózków jak domyślny na liście —
 * parametry z magazynu zastępują `filterDefaultValues`, więc bez niego pole
 * „Czynności wózkowe” byłoby puste.
 */
export function departmentListParams(department: Department) {
  return {
    filter: { dzial: department, forklift: FORKLIFT_FILTER_DEFAULT },
    displayedFilters: {},
  };
}

/**
 * Pierwszy widok listy dla roli. Z przełącznikami `bankRoleDefaultViews`
 * i `bankFtlFilters` (makieta FTL pkt 9.3, 15.10.2026) planista FTL
 * i planista główny dostają widok roli z makiety (`roleDefaultListParams`);
 * inne role — widok działu jak dotąd. `null` = bez domyślnego filtra.
 */
export function firstListParams(role: string | null | undefined, roleViews: boolean) {
  const roleView = roleViews ? roleDefaultListParams(role) : null;
  if (roleView) return roleView;
  const department = defaultDepartmentFor(role);
  return department ? departmentListParams(department) : null;
}

/**
 * Ustawia domyślny widok, jeśli użytkownik nie ma jeszcze własnego. Czeka na
 * przełączniki (`undefined` = jeszcze się wczytują) — inaczej planista
 * dostałby na chwilę widok działu i ten zostałby już w magazynie.
 */
export function useDefaultDepartmentView(): void {
  const { permissions } = usePermissions<string>();
  const store = useStoreContext();
  const roleViewsFlag = useFeatureState(FEATURE_BANK_DEFAULT_VIEWS);
  const filtersFlag = useFeatureState(FEATURE_BANK_FILTERS);
  useEffect(() => {
    if (roleViewsFlag === undefined || filtersFlag === undefined) return;
    const params = firstListParams(permissions, roleViewsFlag && filtersFlag);
    if (!params) return;
    if (store.getItem(BANK_LIST_PARAMS_KEY) !== undefined) return;
    store.setItem(BANK_LIST_PARAMS_KEY, params);
  }, [permissions, store, roleViewsFlag, filtersFlag]);
}
